import { ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import { reportFilterCountryCodeSelector } from '../../state';
import { useReportFilters } from './ReportFiltersProvider';

const TooltipWrapper = ({
  title,
  disabled,
  children,
}: {
  title: string;
  disabled: boolean;
  children: ReactElement;
}) => (!disabled ? <Tooltip title={title}>{children}</Tooltip> : children);

interface OnedriveButtonProps {
  disabled?: boolean;
  hasCountry?: boolean;
}

const OnedriveButton = ({ disabled: externalDisabled, hasCountry }: OnedriveButtonProps) => {
  const dispatch = useDispatch();
  const { loadingSelector, onedriveAction } = useReportFilters();

  const handleClickOnedrive = useCallback(() => {
    if (!onedriveAction) return;
    const action = onedriveAction();
    const actions = Array.isArray(action) ? action : [action];
    actions.forEach(a => dispatch(a));
  }, [dispatch, onedriveAction]);

  const { t } = useTranslation();
  const loading = useSelector(loadingSelector);

  const fallbackCountry = useSelector(reportFilterCountryCodeSelector);
  const effectiveHasCountry = hasCountry ?? Boolean(fallbackCountry);

  const disabled = externalDisabled || !effectiveHasCountry || loading;
  return onedriveAction ? (
    <TooltipWrapper title={t('toolbar.labels.sendToOnedrive') as string} disabled={disabled}>
      <Button color="primary" variant="contained" onClick={handleClickOnedrive} disabled={disabled}>
        <CloudDownloadIcon />
      </Button>
    </TooltipWrapper>
  ) : null;
};

export default OnedriveButton;
